import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Alert, Fab, Stack, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import moment from 'moment';
import './attendance-tracker.daily-care-log.screen.scss';
import AttendanceSessionCard from '../../../../components/attendance-tracker/attendance-session-card';
import {ContentPage} from '../../../../components/content-page/content-page';
import DateCarousel from '../../../../components/daily-care-log/date-carousel/date-carousel';
import {DailyCareLogFiltersDrawer} from '../../../../components/daily-care-log/filters-drawer';
import {TaskFilterHeader} from '../../../../components/daily-care-log/task-table/task-filter-header';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useDailyCareLogFilters} from '../../../../contexts/dailycare-log-filters/context';
import {ScreenPaths} from '../../../../enums';
import {
  AttendanceTrackerSession,
  AttendanceTrackerSessions,
} from '../../../../backend/responses/attendance-tracker-session';
import {translations} from '../../../../i18n/translations';
import {EntityAttendanceTrackerRecordsRepository} from '../../../../repositories/entity-attendance-tracker-records';
import {
  filterSessions,
  formatSessionDuration,
  getSessionSecondsInDay,
  isSessionOverdue,
} from '../../../../utils/functions/attendance-sessions';
import {SkeletonsList} from '../home/home.daily-care-log.screen';
import {useServerNow} from '../../../../hooks/use-server-now';
import {useAttendanceOngoingBarVisible} from '../../../../hooks/use-attendance-ongoing-bar-visible';

export default function AttendanceTrackerDailyCareLogScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {entityId}: {entityId: string} = useParams();
  const dailyCareLogContext = useDailyCareLog();
  const {tasksFilters} = useDailyCareLogFilters();
  const texts = translations.daily_care_log.attendance_tracker;

  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');
  const selectedDay = useMemo(() => {
    if (!date) return moment();
    const d = moment(date);
    return d.isValid() ? d : moment();
  }, [date]);
  // Attendance can only be registered now, so only today has the button
  const isToday = selectedDay.isSame(moment(), 'day');

  const [data, setData] = useState<AttendanceTrackerSessions | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const now = useServerNow(data?.serverTime);
  // The bar above the navigation already has the button to register
  const isOngoingBarVisible = useAttendanceOngoingBarVisible();

  const openSession = (session: AttendanceTrackerSession) =>
    history.push(
      `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_AttendanceTracker}/${session.id}?date=${selectedDay.format('YYYY-MM-DD')}`,
      {session}
    );

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(false);
    dailyCareLogContext.toggleBottomNavigation(true, true);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = useCallback(async () => {
    setData(undefined);
    setHasError(false);

    try {
      setData(
        await EntityAttendanceTrackerRecordsRepository.instance.getMySessions(
          entityId,
          selectedDay.clone().startOf('day').toISOString(),
          selectedDay.clone().endOf('day').toISOString()
        )
      );
    } catch (e) {
      console.error(e);
      setHasError(true);
    }
  }, [entityId, selectedDay]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // The open session is always shown first in today, so it is not forgotten. Filters do not hide it.
  const pinnedSession = isToday ? data?.openSession : undefined;
  const daySessions = useMemo(
    () => (data?.sessions ?? []).filter((session) => session.id !== pinnedSession?.id),
    [data, pinnedSession]
  );
  const filteredSessions = useMemo(
    () =>
      filterSessions(
        daySessions,
        {
          movements: tasksFilters.attendanceMovements,
          status: tasksFilters.attendanceStatus,
          periods: tasksFilters.attendancePeriods,
        },
        selectedDay
      ),
    [daySessions, tasksFilters, selectedDay]
  );

  // Time worked in the day (open sessions go on until now)
  const totalSeconds = useMemo(() => {
    const all = [...(data?.sessions ?? [])];
    if (data?.openSession && !all.some((session) => session.id === data.openSession?.id)) {
      all.push(data.openSession);
    }
    return all.reduce((sum, session) => sum + getSessionSecondsInDay(session, now, selectedDay), 0);
  }, [data, now, selectedDay]);

  const hasFilters =
    tasksFilters.attendanceMovements.length > 0 ||
    tasksFilters.attendanceStatus.length > 0 ||
    tasksFilters.attendancePeriods.length > 0;
  const isEmpty = !pinnedSession && filteredSessions.length === 0;
  // Nothing at all in the day (filters do not count)
  const hasNoSessions = !!data && !data.openSession && data.sessions.length === 0;
  const registerLabel = t(data?.openSession ? texts.register_exit : texts.register_entry);

  return (
    <ContentPage>
      <DateCarousel
        selectedDate={selectedDay}
        onDateChanged={(value) => {
          searchParams.set('date', value.format('YYYY-MM-DD'));
          history.push({search: searchParams.toString()});
        }}
      />
      <Stack className="attendance-tracker-container" justifyContent="center" direction="row">
        <DailyCareLogFiltersDrawer
          type="attendance-tracker"
          open={filtersDrawerOpen}
          activeFilters={tasksFilters}
          onDrawerClose={() => setFiltersDrawerOpen(false)}
          onFiltersSave={() => {}}
        />

        {hasError ? (
          <Stack className="attendance-tracker-empty" alignItems="center">
            <Alert severity="error">{t(texts.load_error)}</Alert>
          </Stack>
        ) : !data ? (
          <SkeletonsList count={6} />
        ) : (
          <Stack className="attendance-tracker-list" gap="10px">
            <TaskFilterHeader
              title={t(texts.title)}
              subtitleDay={selectedDay.format('YYYY-MM-DD')}
              onFilterClick={() => setFiltersDrawerOpen(true)}
            />

            {totalSeconds > 0 && (
              <Typography className="attendance-tracker-total">
                {t(texts.session.total, {duration: formatSessionDuration(totalSeconds)})}
              </Typography>
            )}

            {pinnedSession && isSessionOverdue(pinnedSession, now) && (
              <Alert severity="error">
                {t(texts.session.overdue_alert, {
                  date: moment(pinnedSession.entry.registeredAt).format('DD/MM/YYYY HH:mm'),
                })}
              </Alert>
            )}

            {pinnedSession && (
              <AttendanceSessionCard
                session={pinnedSession}
                now={now}
                selectedDay={selectedDay}
                onSelect={openSession}
              />
            )}

            {filteredSessions.map((session) => (
              <AttendanceSessionCard
                key={session.id}
                session={session}
                now={now}
                selectedDay={selectedDay}
                onSelect={openSession}
              />
            ))}

            {isEmpty && (
              <Stack alignItems="center" flexGrow="1" flexDirection="column">
                <Stack marginTop="auto" marginBottom="auto">
                  <Typography className="empty-title">
                    {hasFilters
                      ? t(translations.daily_care_log.occurrences.empty_filters_title)
                      : t(texts.empty_title)}
                  </Typography>
                  <Typography className="empty-subtitle">
                    {isToday ? t(texts.empty_subtitle) : t(texts.empty_other_day)}
                  </Typography>
                </Stack>
              </Stack>
            )}

            {/* Add bottom space */}
            <Stack sx={{minHeight: 86 + 56 + 18}} />
          </Stack>
        )}

        {isToday && !hasError && !isOngoingBarVisible && (
          <Fab
            color="primary"
            className="attendance-tracker-floating"
            size="large"
            // Like the occurrences: with the label while there is nothing in the list, only the icon otherwise
            variant={hasNoSessions ? 'extended' : 'circular'}
            aria-label={registerLabel}
            onClick={() =>
              history.push(
                `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_NewAttendanceRecord}?date=${selectedDay.format('YYYY-MM-DD')}`
              )
            }
          >
            <Add />
            {hasNoSessions && <Typography>{registerLabel}</Typography>}
          </Fab>
        )}
      </Stack>
    </ContentPage>
  );
}
